import { useEffect, useMemo, useState } from "react";

const PUBLIC_BASE = (process.env.PUBLIC_URL || "").trim();

function publicAsset(path) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (!PUBLIC_BASE || PUBLIC_BASE === ".") return normalizedPath;
  return `${PUBLIC_BASE.replace(/\/$/, "")}${normalizedPath}`;
}

const LOGO_SRC = publicAsset("/icon.png");
const GALLERY_MANIFEST_URL = publicAsset("/gallery-images.json");

export default function HeroGallery() {
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadGallery() {
      try {
        const res = await fetch(GALLERY_MANIFEST_URL, { cache: "no-store" });
        const data = await res.json();
        if (!cancelled && Array.isArray(data)) {
          setImages(data.map((item) => publicAsset(item)));
        }
      } catch {
        if (!cancelled) {
          setImages([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadGallery();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (images.length <= 1) return undefined;

    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % images.length);
    }, 4000);

    return () => window.clearInterval(timer);
  }, [images]);

  const firstTrack = useMemo(() => [...images, ...images], [images]);
  const secondTrack = useMemo(
    () => [...images.slice().reverse(), ...images.slice().reverse()],
    [images]
  );

  const handleSelectImage = (src) => {
    const selectedIndex = images.indexOf(src);
    if (selectedIndex >= 0) {
      setActiveIndex(selectedIndex);
    }
  };

  return (
    <div className="relative mx-auto max-w-xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white/92 p-4 shadow-xl shadow-orange-100/40">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(253,186,116,0.18),_transparent_36%)]" aria-hidden />

      <div className="relative z-10">
        <div className="flex items-center gap-3">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[#171717] shadow-lg">
            <img src={LOGO_SRC} alt="Petrah Collection logo" className="h-11 w-11 object-contain" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#e46a2e]">Style gallery</p>
            <h2 className="mt-1 text-xl font-black tracking-tight text-slate-950">All looks from our image collection</h2>
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-[1.75rem] border border-slate-200 bg-[linear-gradient(180deg,#fff,#f8fafc)] p-3">
          <div className="relative overflow-hidden rounded-[1.25rem] bg-slate-100">
            {images.length > 0 ? (
              <img
                src={images[activeIndex]}
                alt={`Petrah Collection style ${activeIndex + 1}`}
                className="h-64 w-full object-cover sm:h-72"
              />
            ) : (
              <div className="flex h-64 w-full items-center justify-center px-6 text-center text-sm font-medium text-slate-500 sm:h-72">
                {isLoading ? "Loading gallery images..." : "No gallery images found in public/images."}
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/55 via-slate-950/20 to-transparent px-4 py-4">
              <div className="inline-flex rounded-full bg-white/90 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-800">
                Auto preview
              </div>
            </div>
          </div>

          {images.length > 0 ? (
            <div className="mt-4 space-y-3">
              <MarqueeRow images={firstTrack} duration="140s" onSelect={handleSelectImage} />
              <MarqueeRow images={secondTrack} duration="170s" reverse onSelect={handleSelectImage} />
            </div>
          ) : null}
        </div>
      </div>

      <style>{`
        @keyframes hero-gallery-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes hero-gallery-marquee-reverse {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

function MarqueeRow({ images, duration, reverse = false, onSelect }) {
  return (
    <div className="group overflow-hidden">
      <div
        className="flex w-max gap-3 group-hover:[animation-play-state:paused]"
        style={{
          animation: `${reverse ? "hero-gallery-marquee-reverse" : "hero-gallery-marquee"} ${duration} linear infinite`,
        }}
      >
        {images.map((src, index) => (
          <button
            type="button"
            key={`${src}-${index}`}
            className="h-20 w-24 shrink-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:border-[#e46a2e] sm:h-24 sm:w-28"
            onClick={() => onSelect?.(src)}
            aria-label={`Preview gallery image ${index + 1}`}
          >
            <img src={src} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}
